import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAuthContext } from 'src/auth/hooks';
import { useGetAdvancedUserPermissions } from 'src/api/users-entities';

import { useEntityContext } from '../hooks';

export default function GESGuard({ children }) {
  const router = useRouter();
  const { authenticated, user } = useAuthContext();
  const { entity } = useEntityContext();
  const [checked, setChecked] = useState(false);

  const { hasAccess, permissions, parentPermissions, isLoading, error, hasChildAccess } =
    useGetAdvancedUserPermissions(user?.id, entity?.id);

  const check = useCallback(() => {
    if (!authenticated) {
      const searchParams = new URLSearchParams({ returnTo: window.location.pathname }).toString();
      const href = `${paths.auth.jwt.login}?${searchParams}`;
      router.replace(href);
      return;
    }

    if (isLoading) return;

    if (error) {
      router.replace(paths.error.permissionDenied);
      return;
    }

    // Superadmin a toujours accès
    if (user.isSuperadmin) {
      setChecked(true);
      return;
    }

    // Vérifier si l'entité permet l'accès GES
    if (!entity?.can_access_ges) {
      router.replace(paths.error.permissionDenied);
      return;
    }

    // Si c'est un dashboard, autoriser l'accès si l'utilisateur a accès à une entité fille
    if (window.location.pathname.includes('/dashboard')) {
      const canAccessDashboard =
        permissions?.can_access_ges ||
        permissions?.is_admin ||
        parentPermissions?.is_admin ||
        hasChildAccess;

      if (!canAccessDashboard) {
        router.replace(paths.error.permissionDenied);
        return;
      }
    } else {
      // Pour les autres pages, utiliser la logique d'accès standard
      if (!hasAccess) {
        router.replace(paths.error.permissionDenied);
        return;
      }
      setChecked(true);
    }

    setChecked(true);
  }, [
    authenticated,
    isLoading,
    error,
    user.isSuperadmin,
    entity?.can_access_ges,
    router,
    permissions?.can_access_ges,
    permissions?.is_admin,
    parentPermissions?.is_admin,
    hasChildAccess,
    hasAccess,
  ]);

  useEffect(() => {
    check();
  }, [check]);

  if (!checked) return null;

  return <>{children}</>;
}

GESGuard.propTypes = {
  children: PropTypes.node,
};
